import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import toast from 'react-hot-toast';

const LedgerView = () => {
  const [parties, setParties] = useState([]);
  const [selected, setSelected] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deletingTxn, setDeletingTxn] = useState(null);

  useEffect(() => {
    axios.get('https://api.pranucollection.com/api/parties')
      .then(res => setParties(res.data))
      .catch(err => console.error('Error loading parties:', err));

    axios.get('https://api.pranucollection.com/api/ledger/summary')
      .then(res => setSummary(res.data))
      .catch(err => console.error('Error loading summary:', err));
  }, []);

  const loadLedger = async (id) => {
    try {
      setSelected(id);
      const [txnsRes, balRes] = await Promise.all([
        axios.get('https://api.pranucollection.com/api/ledger/take'),
        axios.get(`https://api.pranucollection.com/api/ledger/balance/${id}`)
      ]);

      const filtered = txnsRes.data.filter(t =>
        t.party === id || (t.party && t.party._id === id)
      );

      setTransactions(filtered);
      setBalance(balRes.data.balance);
    } catch (err) {
      console.error('Error loading ledger:', err);
    }
  };

  const confirmDelete = (txn) => {
    setDeletingTxn(txn);
    setShowModal(true);
  };

  const handleDelete = async () => {
    if (!deletingTxn) return;
    try {
      // Send delete request to the backend
      await axios.delete(`https://api.pranucollection.com/api/ledger/${deletingTxn._id}`);

      // Remove the deleted transaction from the UI state
      setTransactions(prev => prev.filter(t => t._id !== deletingTxn._id));

      // Update the balance after deletion
      const balRes = await axios.get(`https://api.pranucollection.com/api/ledger/balance/${selected}`);
      setBalance(balRes.data.balance);

      // Close the modal and reset state
      setShowModal(false);
      setDeletingTxn(null);
      toast.success('Transaction deleted successfully!');
    } catch (err) {
      console.error('Error deleting transaction:', err);
      alert('Failed to delete transaction.');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NP', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <div className="p-4 max-w-6xl mx-auto">

      {/* Delete Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-lg">
            <h2 className="text-lg font-semibold mb-4">Confirm Deletion</h2>
            <Formik
              initialValues={{ confirm: '' }}
              validationSchema={Yup.object({
                confirm: Yup.string()
                  .required('Type DELETE to confirm')
                  .oneOf(['DELETE'], 'You must type DELETE to confirm')
              })}
              onSubmit={handleDelete}
            >
              {({ handleChange, handleBlur, values, errors, touched }) => (
                <Form>
                  <p className="mb-3 text-sm text-gray-700">
                    Are you sure you want to delete this transaction?
                    <br />
                    Type <strong>DELETE</strong> to confirm.
                  </p>
                  <input
                    type="text"
                    name="confirm"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.confirm}
                    className="w-full p-2 border rounded mb-2"
                    placeholder="Type DELETE"
                  />
                  {touched.confirm && errors.confirm && (
                    <div className="text-sm text-red-600 mb-2">{errors.confirm}</div>
                  )}
                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setDeletingTxn(null);
                      }}
                      className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm text-white bg-red-600 rounded hover:bg-red-700"
                    >
                      Delete
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Customer Summary */}
          <div className="p-5 bg-blue-50 border-2 border-blue-200 rounded-xl shadow">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Customer Summary (Total)</h3>
            <p className="text-sm text-gray-600">
              Net Balance:
              <span className={`font-bold ${summary.customers.netBalance === 0 ? 'text-green-600' : summary.customers.netBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.customers.netBalance)}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              Status: <span className="font-bold">{summary.customers.status}</span>
            </p>
          </div>

          {/* Supplier Summary */}
          <div className="p-5 bg-yellow-50 border-2 border-yellow-200 rounded-xl shadow">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Supplier Summary (Total)</h3>
            <p className="text-sm text-gray-600">
              Net Balance:
              <span className={`font-bold ${summary.suppliers.netBalance === 0 ? 'text-green-600' : summary.suppliers.netBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(summary.suppliers.netBalance)}
              </span>
            </p>
            <p className="text-sm text-gray-600">
              Status: <span className="font-bold">{summary.suppliers.status}</span>
            </p>
          </div>
        </div>
      )}


      {/* Business Selection */}
      <div className="mb-8">
        <label className="block text-lg font-medium mb-2">Select Business:</label>
        <select
          onChange={(e) => loadLedger(e.target.value)}
          value={selected}
          className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          <option value="">Choose a business...</option>
          {parties.map(p => (
            <option key={p._id} value={p._id}>
              {p.name} ({p.type.toUpperCase()})
            </option>
          ))}
        </select>
      </div>

      {/* Balance Display */}
      {balance !== null && (
        <div className={`mb-8 p-6 rounded-xl shadow-md ${balance > 0 ? 'bg-green-50 border-2 border-green-200' :
            balance < 0 ? 'bg-red-50 border-2 border-red-200' :
              'bg-gray-50'
          }`}>
          <div className="text-center">
            <h3 className="text-xl font-semibold mb-2">
              {balance > 0 ? 'Money to Take' : balance < 0 ? 'Money to Give' : 'All Settled'}
            </h3>
            <div className={`text-3xl font-bold ${balance > 0 ? 'text-green-600' : balance < 0 ? 'text-red-600' : 'text-gray-600'
              }`}>
              {formatCurrency(Math.abs(balance))}
            </div>
            {balance !== 0 && (
              <p className="mt-2 text-sm text-gray-600">
                {balance > 0 ? 'This business owes you money' : 'You need to pay this business'}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Transactions Table */}
      {transactions.length > 0 && (
        <div className="overflow-x-auto rounded-lg shadow">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Method</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Details</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map(t => (
                <tr key={t._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(t.transactionDate).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${t.type === 'credit' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                      {t.type === 'credit' ? 'Money to Take' : 'Money to Give'}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-semibold ${t.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(t.amount)}
                  </td>
                  <td className="px-4 py-3 capitalize">{t.paymentMethod}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {t.description || 'No additional details'}
                    {t.chequeNumber && ` (Cheque #${t.chequeNumber})`}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => confirmDelete(t)}
                      className="text-red-600 hover:text-red-800 text-sm underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default LedgerView;
